<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-box contact-modal">

      <!-- Close button -->
      <button class="btn-close" @click="close" aria-label="Close modal">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
      </button>

      <div class="modal-layout">
        <!-- Left: Contact Info Panel -->
        <div class="info-panel">
          <div class="info-panel-inner">
            <h4 class="info-title">Get in touch</h4>
            <p class="info-desc">Have a question or need help? We'd love to hear from you. Reach out through any channel below or send us a message.</p>

            <div class="contact-cards">
              <a href="tel:+13473095344" class="contact-card">
                <div class="card-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </div>
                <div class="card-text">
                  <span class="card-label">Phone</span>
                  <span class="card-value">+1 (347) 309-5344</span>
                </div>
                <svg class="card-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7"/><path d="M7 7h10v10"/></svg>
              </a>

              <a href="mailto:hello@renants.com" class="contact-card">
                <div class="card-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                </div>
                <div class="card-text">
                  <span class="card-label">Email</span>
                  <span class="card-value">support@renants.com</span>
                </div>
                <svg class="card-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7"/><path d="M7 7h10v10"/></svg>
              </a>
            </div>

            <div class="response-note">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <span>We typically respond within 24 hours</span>
            </div>
          </div>
        </div>

        <!-- Right: Form Panel -->
        <div class="form-panel">
          <h5 class="form-title">Send a message</h5>

          <form @submit.prevent="sendContact">
            <!-- Honeypot: hidden from real users, bots tend to fill it. -->
            <div class="hp-field" aria-hidden="true">
              <label for="contact-company">Company</label>
              <input
                id="contact-company"
                type="text"
                v-model="contact.company"
                tabindex="-1"
                autocomplete="off"
              />
            </div>

            <div class="form-group" :class="{ 'has-error': errors.email }">
              <label for="contact-email">
                Email <span class="required">*</span>
              </label>
              <input
                id="contact-email"
                type="email"
                v-model="contact.email"
                placeholder="your.email@example.com"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
                @blur="validateEmail"
                @input="errors.email = ''"
                required
              />
              <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
            </div>

            <div class="form-row">
              <div class="form-group" :class="{ 'has-error': errors.phone }">
                <label for="contact-phone">Phone (optional)</label>
                <input
                  id="contact-phone"
                  type="tel"
                  v-model="contact.phone"
                  placeholder="(555) 123-4567"
                  class="form-control"
                  :class="{ 'is-invalid': errors.phone }"
                  @blur="validatePhone"
                  @input="onPhoneInput"
                  maxlength="15"
                />
                <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
              </div>

              <div class="form-group" :class="{ 'has-error': errors.subject }">
                <label for="contact-subject">
                  Subject <span class="required">*</span>
                </label>
                <input
                  id="contact-subject"
                  type="text"
                  v-model="contact.subject"
                  placeholder="How can we help?"
                  class="form-control"
                  :class="{ 'is-invalid': errors.subject }"
                  @blur="validateSubject"
                  @input="errors.subject = ''"
                  required
                />
                <span v-if="errors.subject" class="error-message">{{ errors.subject }}</span>
              </div>
            </div>

            <div class="form-group" :class="{ 'has-error': errors.message }">
              <label for="contact-message">
                Message <span class="required">*</span>
              </label>
              <textarea
                id="contact-message"
                v-model="contact.message"
                placeholder="Tell us more about your inquiry..."
                class="form-control textarea-box"
                :class="{ 'is-invalid': errors.message }"
                @blur="validateMessage"
                @input="errors.message = ''"
                rows="5"
                required
              ></textarea>
              <div class="textarea-footer">
                <span v-if="errors.message" class="error-message">{{ errors.message }}</span>
                <span v-else class="spacer"></span>
                <span class="char-count">{{ contact.message.length }} / 1000</span>
              </div>
            </div>

            <div class="button-group">
              <button type="button" class="btn btn-secondary" @click="close" :disabled="loading">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary" :disabled="loading || !isFormValid">
                <span v-if="loading" class="btn-inner">
                  <svg class="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10" stroke-dasharray="31.4" stroke-dashoffset="10"/></svg>
                  Sending...
                </span>
                <span v-else class="btn-inner">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 2-7 20-4-9-9-4z"/><path d="M22 2 11 13"/></svg>
                  Send Message
                </span>
              </button>
            </div>
          </form>

          <!-- Success Message -->
          <div v-if="showSuccess" class="success-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            <span>Message sent! We'll get back to you soon.</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInst from "@/utils/axiosinstance";
import { toast } from "vue-sonner";
import { trackEvent } from "@/composables/useAnalytics";
import { getRecaptchaToken, recaptchaBlocked, RECAPTCHA_BLOCKED_MESSAGE } from "@/utils/recaptcha";
import { trackPageVisit } from "@/utils/trackPageVisit";

export default {
  emits: ["close"],
  data() {
    return {
      contact: {
        email: "",
        phone: "",
        subject: "",
        message: "",
        company: "" // honeypot — must stay empty
      },
      errors: {
        email: "",
        phone: "",
        subject: "",
        message: ""
      },
      loading: false,
      showSuccess: false
    };
  },

  computed: {
    isFormValid() {
      return (
        this.contact.email &&
        this.contact.subject &&
        this.contact.message &&
        !this.errors.email &&
        !this.errors.phone &&
        !this.errors.subject &&
        !this.errors.message
      );
    }
  },

  mounted() {
    window.addEventListener("keydown", this.handleEsc);
    // Covers both the navbar Contact popup and the /contact-us page.
    trackPageVisit("Contact Us");
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.handleEsc);
  },

  methods: {
    close() {
      document.activeElement?.blur();
      this.$emit("close");
    },

    handleEsc(e) {
      if (e.key === "Escape") {
        this.close();
      }
    },

    validateEmail() {
      if (!this.contact.email) {
        this.errors.email = "Email is required";
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.contact.email)) {
        this.errors.email = "Please enter a valid email address";
        return false;
      }
      this.errors.email = "";
      return true;
    },

    validatePhone() {
      if (this.contact.phone) {
        const digitsOnly = this.contact.phone.replace(/\D/g, '');
        if (digitsOnly.length < 10) {
          this.errors.phone = "Phone number must be at least 10 digits";
          return false;
        }
        if (digitsOnly.length > 10) {
          this.errors.phone = "Phone number must not exceed 10 digits";
          return false;
        }
      }
      this.errors.phone = "";
      return true;
    },

    validateSubject() {
      if (!this.contact.subject) {
        this.errors.subject = "Subject is required";
        return false;
      }
      if (this.contact.subject.length < 3) {
        this.errors.subject = "Subject must be at least 3 characters";
        return false;
      }
      this.errors.subject = "";
      return true;
    },

    validateMessage() {
      if (!this.contact.message) {
        this.errors.message = "Message is required";
        return false;
      }
      if (this.contact.message.length < 10) {
        this.errors.message = "Message must be at least 10 characters";
        return false;
      }
      if (this.contact.message.length > 1000) {
        this.errors.message = "Message must not exceed 1000 characters";
        return false;
      }
      this.errors.message = "";
      return true;
    },

    validateForm() {
      const isEmailValid = this.validateEmail();
      const isPhoneValid = this.validatePhone();
      const isSubjectValid = this.validateSubject();
      const isMessageValid = this.validateMessage();
      return isEmailValid && isPhoneValid && isSubjectValid && isMessageValid;
    },

    async sendContact() {
      if (!this.validateForm()) {
        toast.error("Please fix the errors in the form");
        return;
      }

      this.loading = true;
      try {
        const recaptchaToken = await getRecaptchaToken('contact');
        if (recaptchaBlocked(recaptchaToken)) {
          toast.error(RECAPTCHA_BLOCKED_MESSAGE, { duration: 10000 });
          return;
        }
        await axiosInst.post('/api/send-email', {
          to: this.contact.email,
          phone: this.contact.phone,
          subject: this.contact.subject,
          message: this.contact.message,
          company: this.contact.company, // honeypot
          recaptchaToken,
        });

        this.showSuccess = true;
        trackEvent('contact_submit', { subject: this.contact.subject });
        toast.success("Message sent successfully!");

        setTimeout(() => {
          this.contact = { email: '', phone: '', message: '', subject: '', company: '' };
          this.showSuccess = false;
          this.close();
        }, 2000);
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to send message. Please try again.";
        toast.error(errorMessage);
      } finally {
        this.loading = false;
      }
    },

    onPhoneInput(e) {
      let digits = e.target.value.replace(/\D/g, '');
      if (digits.length > 10) digits = digits.slice(0, 10);
      this.contact.phone = digits;
      this.errors.phone = '';
    },
  },
};
</script>

<style scoped>
/* ── Overlay ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
  animation: fadeIn 0.2s ease;
  backdrop-filter: blur(6px);
}

/* ── Modal Box ── */
.modal-box {
  background: #fff;
  border-radius: 20px;
  width: 820px;
  max-width: 96%;
  max-height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0,0,0,0.04);
  animation: modalIn 0.3s ease-out;
  position: relative;
}

/* ── Close ── */
.btn-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  border: none;
  background: rgba(255,255,255,0.8);
  backdrop-filter: blur(4px);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}
.btn-close:hover {
  background: #f1f5f9;
  color: #1e293b;
  transform: scale(1.05);
}

/* ── Two-column layout ── */
.modal-layout {
  display: flex;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

/* ── Left: Info Panel ── */
.info-panel {
  width: 300px;
  flex-shrink: 0;
  background: linear-gradient(160deg, #1d3fa8 0%, #3467d5 45%, #5b87f0 100%);
  padding: 36px 28px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
}

.info-panel-inner {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.info-title {
  font-family: var(--font-primary);
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.info-desc {
  font-family: var(--font-primary);
  font-size: 14px;
  color: rgba(255, 255, 255, 0.78);
  margin: 0;
  line-height: 1.6;
}

/* ── Contact Cards ── */
.contact-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.contact-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.15);
  text-decoration: none;
  transition: all 0.2s;
  cursor: pointer;
}
.contact-card:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.card-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
}

.card-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.card-label {
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.6);
}

.card-value {
  font-family: var(--font-primary);
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-arrow {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.4);
  transition: all 0.2s;
}
.contact-card:hover .card-arrow {
  color: #fff;
  transform: translate(2px, -2px);
}

/* ── Response Note ── */
.response-note {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-primary);
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.55);
  padding-top: 4px;
}
.response-note svg {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.45);
}

/* ── Right: Form Panel ── */
.form-panel {
  flex: 1;
  padding: 32px 32px 28px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.form-title {
  font-family: var(--font-primary);
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 20px 0;
  letter-spacing: -0.2px;
}

/* ── Form ── */
form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
}

/* ── Honeypot (off-screen, not display:none so bots still fill it) ── */
.hp-field {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.form-row {
  display: flex;
  gap: 12px;
}
.form-row .form-group {
  flex: 1;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  font-family: var(--font-primary);
  margin: 0;
}

.required {
  color: #ef4444;
  margin-left: 2px;
}

/* ── Inputs ── */
.form-control {
  width: 100%;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  font-size: 14px;
  font-family: var(--font-primary);
  color: var(--color-text-primary);
  background: #f8fafc;
  transition: all 0.2s;
  box-sizing: border-box;
}
.form-control:focus {
  outline: none;
  border-color: #3467d5;
  box-shadow: 0 0 0 3px rgba(52, 103, 213, 0.1);
  background: #fff;
}
.form-control::placeholder {
  color: #94a3b8;
}
.form-control.is-invalid {
  border-color: #ef4444;
}
.form-control.is-invalid:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.textarea-box {
  min-height: 100px;
  resize: none;
  font-family: var(--font-primary);
  line-height: 1.5;
}

.textarea-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -2px;
}
.spacer { flex: 1; }

/* ── Error ── */
.error-message {
  font-size: 12px;
  color: #ef4444;
  font-family: var(--font-primary);
}

/* ── Char count ── */
.char-count {
  font-size: 11px;
  color: #94a3b8;
  font-family: var(--font-primary);
}

/* ── Buttons ── */
.button-group {
  display: flex;
  gap: 10px;
  margin-top: 6px;
}

.btn {
  flex: 1;
  padding: 12px 20px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  font-family: var(--font-primary);
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  height: 46px;
}
.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, #1d3fa8 0%, #3467d5 100%);
  color: #fff;
  box-shadow: 0 2px 10px rgba(52, 103, 213, 0.25);
  border: 1.5px solid transparent;
}
.btn-primary:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(52, 103, 213, 0.35);
  transform: translateY(-1px);
}

.btn-secondary {
  background: #fff;
  color: var(--color-text-primary);
  border: 1.5px solid #e2e8f0;
}
.btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: var(--color-text-primary);
}

/* ── Spinner ── */
.spinner {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Success ── */
.success-banner {
  margin-top: 16px;
  padding: 14px 16px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  color: #166534;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-primary);
  font-size: 13.5px;
  font-weight: 500;
  animation: slideUp 0.3s ease-out;
}

/* ── Animations ── */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes modalIn {
  from { transform: translateY(20px) scale(0.97); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}
@keyframes slideUp {
  from { transform: translateY(8px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* ── Tablet ── */
@media (min-width: 601px) and (max-width: 900px) {
  .modal-box {
    width: 94%;
    max-width: 600px;
  }
  .modal-layout {
    flex-direction: column;
  }
  .info-panel {
    width: 100%;
    padding: 24px 24px;
    border-radius: 0;
  }
  .info-title { font-size: 20px; }
  .info-desc { font-size: 13px; }
  .contact-cards {
    flex-direction: row;
    gap: 8px;
  }
  .contact-card { flex: 1; }
  .form-panel { padding: 24px 24px 20px; }
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .modal-overlay {
    padding: 0;
    align-items: flex-start;
  }
  .modal-box {
    width: 100%;
    max-width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }
  /* Full-bleed modal starts at y=0 — keep the X below the notch */
  .btn-close {
    top: calc(16px + env(safe-area-inset-top, 0px));
  }
  .modal-layout {
    flex-direction: column;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    flex: 1;
    min-height: 0;
    padding-bottom: 24px;
  }
  .info-panel {
    width: 100%;
    padding: 24px 20px;
    flex-shrink: 0;
  }
  .info-title { font-size: 20px; }
  .info-desc { font-size: 13px; }
  .contact-cards {
    flex-direction: column;
    gap: 8px;
  }
  .form-panel {
    padding: 20px 20px calc(40px + env(safe-area-inset-bottom, 0px));
    overflow-y: visible;
    flex-shrink: 0;
  }
  .form-row {
    flex-direction: column;
    gap: 14px;
  }
  .form-control {
    font-size: 16px; /* Prevents zoom on iOS */
  }
  .textarea-box {
    min-height: 80px;
  }
  .button-group {
    flex-direction: column-reverse;
    gap: 8px;
    margin-top: 10px;
  }
  .btn {
    width: 100%;
    flex: none;
  }
  .btn-close {
    background: rgba(0, 0, 0, 0.08);
    color: #fff;
  }
  .btn-close:hover {
    background: rgba(0, 0, 0, 0.15);
    color: #fff;
  }
}
</style>
